import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  Action,
  combineReducers,
  configureStore,
  ThunkAction,
} from '@reduxjs/toolkit';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import aiChatbotReducer from '../aiChatbot/slice';
import { ActionType } from '../app/actionType';
import appReducer from '../app/slice'; // moved the last, the above must be imported first
import mediaReducer from '../common/slice';
import pollsReducer from '../community/polls/slice';
import postReducer from '../community/posts/slice';
import postCommentsReducer from '../community/replies/slice';
import roomReducer from '../community/rooms/slice';
import companionsReducer from '../companions/slice';
import conversationsReducer from '../conversations/slice';
import feedbackReducer from '../feedback/slice';
import consultationReducer from '../healthJourney/consultation/slice';
import diagnosisReducer from '../healthJourney/diagnosis/slice';
import enhancedSurveySubmissionsReducer from '../healthJourney/enhancedSurveySubmissions/slice';
import isabelReducer from '../healthJourney/isabel/slice';
import journalReducer from '../healthJourney/journal/slice';
import medicationReducer from '../healthJourney/medication/slice';
import healthJourneyReducer from '../healthJourney/slice';
import symptomReducer from '../healthJourney/symptom/slice';
import treatmentReducer from '../healthJourney/treatment/slice';
import icareReducer from '../icare/slice';
import moodTrackerReducer from '../moodTracker/slice';
import notificationsReducer from '../notifications/slice';
import profileReducer from '../profile/slice';
import searchReducer from '../search/slice';
import storiesCommentsReducer from '../stories/comments/slice';
import storiesReducer from '../stories/stories/slice';
import { devToolsOptions } from './devTools';

const combinedReducer = combineReducers({
  app: appReducer,
  profile: profileReducer,
  healthJourney: healthJourneyReducer,
  symptom: symptomReducer,
  diagnosis: diagnosisReducer,
  treatment: treatmentReducer,
  medication: medicationReducer,
  consultation: consultationReducer,
  journal: journalReducer,
  enhancedSurveySubmissions: enhancedSurveySubmissionsReducer,
  moodTracker: moodTrackerReducer,
  room: roomReducer,
  post: postReducer,
  polls: pollsReducer,
  postComments: postCommentsReducer,
  companions: companionsReducer,
  conversations: conversationsReducer,
  aiChatbot: aiChatbotReducer,
  icare: icareReducer,
  notifications: notificationsReducer,
  stories: storiesReducer,
  storiesComments: storiesCommentsReducer,
  search: searchReducer,
  feedback: feedbackReducer,
  isabel: isabelReducer,
  media: mediaReducer,
});

const rootReducer = (state: any, action: any) => {
  if (action.type === ActionType.logout) {
    const preservedLanguage = state?.app.language;

    const resetState = combinedReducer(undefined, action);

    return {
      ...resetState,
      app: {
        ...resetState.app,
        language: preservedLanguage,
      },
    };
  }
  return combinedReducer(state, action);
};

const persistConfig = {
  key: 'root',
  storage: AsyncStorage,
  whitelist: ['app'],
};

const persistedRootReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedRootReducer,
  // https://redux-toolkit.js.org/api/serializabilityMiddleware - we need to prevent some warnings on RN
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: { warnAfter: 128 },
      serializableCheck: {
        warnAfter: 128,
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools: devToolsOptions,
});

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
